import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { MainLayout } from './components/layout/MainLayout';

// Import all page components
import HomePage from './app/page';
import LoginPage from './app/login/page';
import LoginTotpPage from './app/login/totp/page';
import LoginMfaSetupPage from './app/login/mfa-setup/page';
import ForgotPasswordPage from './app/forgot-password/page';
import DashboardPage from './app/dashboard/page';
import CustomersPage from './app/customers/page';
// import CustomerCreatePage from './app/customers/create/page';
import CustomerDetailPage from './app/customers/[uuid]/page';
import CustomerUsersPage from './app/customer-users/page';
import { UsersListPage } from './components/user/UsersListPage';
import { CreateScopedOrganizationalUserPage } from './components/user/CreateScopedOrganizationalUserPage';
import UserDetailPage from './app/users/[id]/page';
import OrganizationCreateIsoPage from './app/organizations/create/iso/page';
import OrganizationCreateMerchantPage from './app/organizations/create/merchant/page';
import OrganizationDetailPage from './app/organizations/[id]/page';
import OrganizationDocumentTemplatesPage from './app/organizations/[id]/document-templates/page';
import MerchantChecklistPage from './app/organizations/[id]/merchant-checklist/page';
import InternalUsersCreatePage from './app/internal-users/create/page';
import SessionsPage from './app/sessions/page';
import DiagnosticsPage from './app/diagnostics/page';
import AuditLogPage from './app/audit-log/page';
import AllowedWebsiteTemplatesPage from './app/allowed-website-templates/page';
import CreateAllowedWebsiteTemplatePage from './app/allowed-website-templates/create/page';
import AllowedWebsiteTemplateDetailPage from './app/allowed-website-templates/[templateUuid]/page';
import ProfilePage from './app/profile/page';
import ProfileEmailsPage from './app/profile/emails/page';
import ProfileOrganizationPage from './app/profile/organization/page';
import ProfilePasswordPage from './app/profile/password/page';
import ProfileSecurityPage from './app/profile/security/page';
import CustomerPaymentMethodsPage from './app/customer/payment-methods/page';
import CustomerDashboardPage from './app/customer/dashboard/page';
import CustomerPaymentRequestsPage from './app/customer/payment-requests/page';
import CustomerPaymentRequestDetailPage from './app/customer/payment-requests/[uuid]/page';
import CustomerPaymentHistoryPage from './app/customer/payment-history/page';
import RegisterPage from './app/register/[token]/page';
import CustomerSelfOnboardingPage from './app/register/customer/page';
import ResetPasswordPage from './app/reset-password/[token]/page';
import VerifyEmailPage from './app/verify-email/[token]/page';
import PaymentLinkPage from './app/pay/[token]/page';
import PaymentSuccessPage from './app/pay/success/page';
import PaymentRequestsPage from './app/payment-requests/page';
import MerchantPaymentRequestDetailPage from './app/payment-requests/[uuid]/page';
import MerchantBackupPage from './app/merchant-backup/page';
import SingleUseTokensPage from './app/single-use-tokens/page';
import UserManagementTokensPage from './app/user-management-tokens/page';
import EInvoiceTokensPage from './app/e-invoice-tokens/page';
import DownloadsPage from './app/downloads/page';
import PaymentTransactionsPage from './app/payment-transactions/page';
import PaymentTransactionDetailPage from './app/payment-transactions/[id]/page';
import PaymentTransactionLifecycleEventsPage from './app/payment-transaction-lifecycle-events/page';
import CardVaultPage from './app/card-vault/page';
import CardVaultTransactionDetailPage from './app/card-vault/[id]/page';
import { userApi } from './lib/pillarApi';
import { InternalRole } from '@pillar-portal/database/dist/frontend';
import type { Role, UserProfileDetailedPublicResponse } from '@pillar-portal/api-types';
import { OrganizationsListPage } from './components/organization/OrganizationsListPage';
import { PolicyPermissionBundleRoleAssignmentsPage } from './components/permissions/PolicyPermissionBundleRoleAssignmentsPage';
import { PermissionAuditPage } from './components/permissions/PermissionAuditPage';
import { CredentialRotationHistoryPage } from './components/ipx-credentials/CredentialRotationHistoryPage';
import { IpxCredentialPolicyDetailPage } from './components/ipx-credentials/IpxCredentialPolicyDetailPage';
import { LegalDocumentsListPage } from './components/legal/LegalDocumentsListPage';
import { LegalDocumentCreatePage } from './components/legal/LegalDocumentCreatePage';
import { LegalDocumentDetailPage } from './components/legal/LegalDocumentDetailPage';
import { LegalWorkflowPage } from './components/legal/LegalWorkflowPage';
import {
    EncryptionKeyDetailPage,
    EncryptionKeysListPage,
} from './components/encryption-keys/EncryptionKeysPage';
import {
    LogicalTerminalsListPage,
    MerchantAccountsListPage,
    ProcessorsListPage,
    TerminalDevicesListPage,
} from './components/payments-infrastructure/PaymentsInfrastructureListPages';
import {
    CreateLogicalTerminalPage,
    CreateMerchantAccountPage,
    CreateProcessorPage,
    CreateTerminalDevicePage,
    LogicalTerminalDetailPage,
    MerchantAccountDetailPage,
    ProcessorDetailPage,
    TerminalDeviceDetailPage,
} from './components/payments-infrastructure/PaymentsInfrastructureDetailAndCreatePages';
import { UnsavedChangesGuardProvider } from './lib/hooks/useUnsavedChangesGuard';

const ORGANIZATIONAL_USERS_ROLE_LABELS: Partial<Record<Role, string>> = {
    PRINCIPAL: 'Principal',
    ADMIN: 'Admin',
    REP: 'Rep',
};

function GuardedPage(props: { children: ReactNode }) {
    return <UnsavedChangesGuardProvider>{props.children}</UnsavedChangesGuardProvider>;
}

export function App() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/totp" element={<LoginTotpPage />} />
            <Route path="/login/mfa-setup" element={<LoginMfaSetupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/register/customer" element={<CustomerSelfOnboardingPage />} />
            <Route path="/register/:token" element={<RegisterPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/pay" element={<PaymentLinkPage />} />
            <Route path="/pay/success" element={<PaymentSuccessPage />} />
            <Route path="/pay/:token" element={<PaymentLinkPage />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/customer/dashboard" element={<CustomerDashboardPage />} />
            <Route path="/customer/payment-requests" element={<CustomerPaymentRequestsPage />} />
            <Route
                path="/customer/payment-requests/:uuid"
                element={<CustomerPaymentRequestDetailPage />}
            />
            <Route path="/customer/payment-history" element={<CustomerPaymentHistoryPage />} />
            <Route path="/payment-requests" element={<PaymentRequestsPage />} />
            <Route
                path="/payment-requests/:uuid"
                element={<MerchantPaymentRequestDetailPage />}
            />
            <Route
                path="/merchant/payment-requests"
                element={<Navigate to="/payment-requests" replace />}
            />
            <Route
                path="/merchant/payment-requests/:uuid"
                element={<LegacyMerchantPaymentRequestDetailRedirect />}
            />
            <Route path="/customers" element={<CustomersPage />} />
            {/* <Route path="/customers/create" element={<CustomerCreatePage />} /> */}
            <Route path="/customers/:uuid" element={<CustomerDetailPage />} />
            <Route path="/customer-users" element={<CustomerUsersPage />} />
            <Route
                path="/users"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <UsersListPage<UserProfileDetailedPublicResponse, string>
                                key="merchant-users"
                                title="Users"
                                cardTitle="Merchant Users"
                                createPath="/users/create"
                                createButtonLabel="Create User"
                                roles={ORGANIZATIONAL_USERS_ROLE_LABELS}
                                queryKeyPrefix={['userListing', 'merchant_users']}
                                fetchPage={userApi.listMerchantUsersPage}
                                parseCursorParam={(pageParam: unknown) => {
                                    if (pageParam === null || pageParam === undefined) {
                                        return undefined;
                                    }
                                    const parseResult = z.string().trim().min(1).safeParse(pageParam);
                                    if (!parseResult.success) {
                                        throw new Error('Expected opaque cursor');
                                    }
                                    return parseResult.data;
                                }}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/iso-users"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <UsersListPage<UserProfileDetailedPublicResponse, string>
                                key="iso-users"
                                title="ISO Users"
                                cardTitle="ISO Users"
                                createPath="/iso-users/create"
                                createButtonLabel="Create ISO User"
                                roles={ORGANIZATIONAL_USERS_ROLE_LABELS}
                                queryKeyPrefix={['userListing', 'iso_users']}
                                fetchPage={userApi.listIsoUsersPage}
                                parseCursorParam={(pageParam: unknown) => {
                                    if (pageParam === null || pageParam === undefined) {
                                        return undefined;
                                    }
                                    const parseResult = z.string().trim().min(1).safeParse(pageParam);
                                    if (!parseResult.success) {
                                        throw new Error('Expected opaque cursor');
                                    }
                                    return parseResult.data;
                                }}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/users/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <CreateScopedOrganizationalUserPage
                                organizationCapacityKey="is_merchant"
                                pageTitle="Create User"
                                backHref="/users"
                                backTooltip="Back to Users"
                                createButtonLabel="Create User"
                                invalidateQueryKeys={[
                                    ['userListing', 'merchant_users'],
                                    ['userListing', 'iso_users'],
                                    ['userListing', 'internal_users'],
                                ]}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/iso-users/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <CreateScopedOrganizationalUserPage
                                organizationCapacityKey="is_iso"
                                pageTitle="Create ISO User"
                                backHref="/iso-users"
                                backTooltip="Back to ISO Users"
                                createButtonLabel="Create ISO User"
                                invalidateQueryKeys={[
                                    ['userListing', 'merchant_users'],
                                    ['userListing', 'iso_users'],
                                    ['userListing', 'internal_users'],
                                ]}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/users/:id"
                element={
                    <GuardedPage>
                        <UserDetailPage />
                    </GuardedPage>
                }
            />
            <Route
                path="/organizations/isos"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <OrganizationsListPage
                                key="iso-organizations"
                                organizationCapacityKey="is_iso"
                                title="ISO Organizations"
                                cardTitle="ISOs"
                                createPath="/organizations/create/iso"
                                createButtonLabel="Create ISO"
                                queryKeyPrefix={['organizationListing', 'isos']}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organizations/merchants"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <OrganizationsListPage
                                key="merchant-organizations"
                                organizationCapacityKey="is_merchant"
                                title="Merchants"
                                cardTitle="Merchants"
                                createPath="/organizations/create/merchant"
                                createButtonLabel="Create Merchant"
                                queryKeyPrefix={['organizationListing', 'merchants']}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organizations/customers"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <OrganizationsListPage
                                key="customer-organizations"
                                organizationCapacityKey="is_customer"
                                title="Customer Organizations"
                                cardTitle="Customer Organizations"
                                queryKeyPrefix={['organizationListing', 'customers']}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route path="/organizations/create/iso" element={<OrganizationCreateIsoPage />} />
            <Route
                path="/organizations/create/merchant"
                element={<OrganizationCreateMerchantPage />}
            />
            <Route
                path="/organizations/:id/permissions-policies/:policyUuid/users"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <PolicyPermissionBundleRoleAssignmentsPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organizations/:id/permissions-audit"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <PermissionAuditPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organizations/:id/document-templates"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <OrganizationDocumentTemplatesPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organizations/:id/credential-policies/:policyUuid/credentials/:credentialUuid/rotation-history"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <CredentialRotationHistoryPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organizations/:id/credential-policies/:policyUuid"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <IpxCredentialPolicyDetailPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organizations/:id"
                element={
                    <GuardedPage>
                        <OrganizationDetailPage />
                    </GuardedPage>
                }
            />
            <Route
                path="/organizations/:id/merchant-checklist"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <MerchantChecklistPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/merchant-backup"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <MerchantBackupPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/internal-users"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <UsersListPage<UserProfileDetailedPublicResponse, string>
                                key="internal-users"
                                title="Internal Users"
                                cardTitle="Superadmins and Internal Admins"
                                createPath="/internal-users/create"
                                createButtonLabel="Create Internal User"
                                roles={InternalRole}
                                queryKeyPrefix={['userListing', 'internal_users']}
                                fetchPage={userApi.listInternalUsersPage}
                                parseCursorParam={(pageParam: unknown) => {
                                    if (pageParam === null || pageParam === undefined) {
                                        return undefined;
                                    }
                                    const parseResult = z.string().trim().min(1).safeParse(pageParam);
                                    if (!parseResult.success) {
                                        throw new Error('Expected opaque cursor');
                                    }
                                    return parseResult.data;
                                }}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route path="/internal-users/create" element={<InternalUsersCreatePage />} />
            <Route path="/sessions" element={<SessionsPage />} />
            <Route path="/single-use-tokens" element={<SingleUseTokensPage />} />
            <Route path="/user-management-tokens" element={<UserManagementTokensPage />} />
            <Route path="/e-invoice-tokens" element={<EInvoiceTokensPage />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/payment-transactions" element={<PaymentTransactionsPage />} />
            <Route
                path="/payment-transaction-lifecycle-events"
                element={<PaymentTransactionLifecycleEventsPage />}
            />
            <Route path="/card-vault" element={<CardVaultPage />} />
            <Route
                path="/payment-transactions/:id"
                element={<PaymentTransactionDetailPage />}
            />
            <Route path="/card-vault/:id" element={<CardVaultTransactionDetailPage />} />
            <Route path="/diagnostics" element={<DiagnosticsPage />} />
            <Route
                path="/encryption-keys"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <EncryptionKeysListPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/encryption-keys/:secretName"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <EncryptionKeyDetailPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route
                path="/allowed-website-templates"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <AllowedWebsiteTemplatesPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/allowed-website-templates/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <CreateAllowedWebsiteTemplatePage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/allowed-website-templates/:templateUuid"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <AllowedWebsiteTemplateDetailPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <GuardedPage>
                        <ProfilePage />
                    </GuardedPage>
                }
            />
            <Route path="/profile/emails" element={<ProfileEmailsPage />} />
            <Route path="/profile/organization" element={<ProfileOrganizationPage />} />
            <Route path="/profile/password" element={<ProfilePasswordPage />} />
            <Route path="/profile/security" element={<ProfileSecurityPage />} />
            <Route path="/customer/payment-methods" element={<CustomerPaymentMethodsPage />} />
            <Route
                path="/payments-infrastructure/processors"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <ProcessorsListPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/processors/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <CreateProcessorPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/processors/:processorName"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <ProcessorDetailPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/merchant-accounts"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <MerchantAccountsListPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/merchant-accounts/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <CreateMerchantAccountPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/merchant-accounts/:merchantAccountId"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <MerchantAccountDetailPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/logical-terminals"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <LogicalTerminalsListPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/logical-terminals/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <CreateLogicalTerminalPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/logical-terminals/:logicalTerminalId"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <LogicalTerminalDetailPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/terminal-devices"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <TerminalDevicesListPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/terminal-devices/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <CreateTerminalDevicePage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payments-infrastructure/terminal-devices/:terminalDeviceId"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <GuardedPage>
                                <TerminalDeviceDetailPage />
                            </GuardedPage>
                        </MainLayout>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/legal-documents"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <LegalDocumentsListPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/legal-documents/create"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <LegalDocumentCreatePage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/legal-documents/:id"
                element={
                    <ProtectedRoute>
                        <MainLayout>
                            <LegalDocumentDetailPage />
                        </MainLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/legal-workflow"
                element={
                    <ProtectedRoute>
                        <LegalWorkflowPage />
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
}

function LegacyMerchantPaymentRequestDetailRedirect() {
    const params = useParams();
    const paymentRequestUuid = params.uuid;

    if (paymentRequestUuid === undefined || paymentRequestUuid.trim().length === 0) {
        return <Navigate to="/payment-requests" replace />;
    }

    return (
        <Navigate
            to={`/payment-requests/${paymentRequestUuid}`}
            replace
        />
    );
}
